import { FC, useState } from "react"

import { useQueryExportGroupsTask } from "api/reactQuery/queries/export"
import { ExportType } from "api/reactQuery/queries/export.types"
import {
  ExportConfirmDialog,
  ExportMenu,
  ExportPendingBanner,
} from "components/common/exportMenu"
import { useExportGuard, useExportTasks } from "hooks/exportReports"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { getErrorMessageFromTab } from "utils/api"

interface IProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  classId: number
  groupIds?: number[]
  isExportDisabled: boolean
}

const ExportGroupsMenu: FC<IProps> = ({
  anchorEl,
  open,
  onClose,
  classId,
  groupIds,
  isExportDisabled,
}) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()

  const [isExportDataGroups, setIsExportDataGroups] = useState(false)

  const {
    successTasks: successTasksGroups,
    pendingTasks: pendingTasksGroups,
    setExportReportIdToDownload: setExportReportIdToDownloadGroups,
    handleExportSuccess: handleExportSuccessGroups,
    removeTask: removeTaskGroups,
  } = useExportTasks({ exportType: ExportType.CLASS_GROUPS, classId })

  useQueryExportGroupsTask({
    classId,
    group_ids: groupIds,
    options: {
      enabled: isExportDataGroups,
      onSuccess: (response) => {
        setIsExportDataGroups(false)
        handleExportSuccessGroups(response, classId)
        onExportTriggeredGroups()
        showSnackbar({ title: t("export.fileIsCurrentlyBeingGenerated") })
      },
      onError: (error) =>
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
          variant: "error",
        }),
    },
  })

  const {
    isGenerateDisabled: isDisabledGroups,
    isConfirmDialogOpen: isConfirmDialogOpenGroups,
    handleCloseConfirmDialog: handleCloseConfirmDialogGroups,
    handleConfirm: handleConfirmGroups,
    handleGenerate: guardGenerateGroups,
    onExportTriggered: onExportTriggeredGroups,
  } = useExportGuard({
    hasPendingTasks: pendingTasksGroups.length > 0,
    queryParams: { group_ids: groupIds },
  })

  return (
    <>
      <ExportMenu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        onGenerateNewFile={() =>
          guardGenerateGroups(() => {
            setIsExportDataGroups(true)
          })
        }
        onFileClick={setExportReportIdToDownloadGroups}
        onFileRemove={removeTaskGroups}
        wrapFilename
        successTasks={successTasksGroups}
        isGenerateDisabled={isExportDisabled || isDisabledGroups}
        generateDisabledTooltip={
          isExportDisabled
            ? t("group.allGroupsHiddenTitle")
            : t("export.reportCurrentlyBeingGenerated")
        }
      />

      <ExportConfirmDialog
        open={isConfirmDialogOpenGroups}
        onClose={handleCloseConfirmDialogGroups}
        onConfirm={handleConfirmGroups}
      />

      <ExportPendingBanner visible={pendingTasksGroups.length > 0} />
    </>
  )
}

export default ExportGroupsMenu
